import { BreadcrumbComponent, Button, CIActionGuard, CIAppliedFilter, CIAppliedListFilter, CIPageHeader, SearchInput, toLocalDate, useToastMessage, useUserPermission } from 'ccs-ui-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { debounce } from 'lodash';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import Filter, { statusList } from '../../../../_components/filter/Filter';
import { FILTER_TYPE, SORT_TYPE, URL_PATH, allStatusList } from '../../../../constants';
import { ButtonProp, IRecordModel } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import useExportServiceConfiguration from '../../../../store/_api/services/ExportServiceConfiguration';
import { clearAllServiceList, getAllServiceRequest } from '../../../../store/actions/sagaActions/services/GetAllServiceAction';

import { clearImportServiceSummary } from '../../../../store/reducer/services/ImportServiceSummaryReducer';
import { addClearData, addFilterServiceData, clearFilterService } from '../../../../store/reducer/services/ServiceFilterReducer';
import { arrayBufferToString, defaultServiceListStatus, defaultServiceListStatusArray, exportConfigConst } from '../../../../utilities/services/ServiceListUtils';
import { ServiceList } from '../list-service/ServiceList';

import WarningMessage from '../../../../_components/warning-message/WarningMessage';
import { DEPENDENCY_ACTIONS, dependencyGraphInitialState, getDependencyGraphStateReducer } from '../../../../utilities/RegistryDependancyManager';
import classes from './ServiceListWrapper.module.scss';

interface IFilterData {
  status: string,
  serviceName: string,
  fromDate: string,
  toDate: string,
  sort: string,
  type: string
}

const ServiceListWrapper = () => {
  /* Hooks */
  const { permissionObject } = useUserPermission();
  const history = useNavigate();
  const route = useLocation();
  const { success, failure, renderToast } = useToastMessage();
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const { exportServiceConfiguration } = useExportServiceConfiguration();
  /* Local states */
  const [selectedServiceList, setSelectedServiceList] = useState<IRecordModel[]>([]);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);
  /* Get data from redux store */
  const { serviceList: listServices } = useSelector(
    (state) => state.fetchAllServiceReducer
  );
  const { serviceList } = useSelector((state) => state.fetchAllServiceReducer);
  const { searchText, status, fromDate, toDate, sort } = useSelector((state) => state.filterServiceReducer);
  const [dependencyState, dispatchDependencyState] = useReducer(getDependencyGraphStateReducer, dependencyGraphInitialState);
  /* Local Variables */
  const debounceDelay = 300;
  const removeQuestionMark = route.search.substring(route.search.indexOf('?') + 1);

  let params: IFilterData = {
    status: '',
    serviceName: '',
    fromDate: '',
    toDate: '',
    sort: '',
    type: ''
  };
  try {
    params = JSON.parse(`{"${decodeURIComponent(removeQuestionMark.replace(/&/g, '","').replace(/=/g, '":"'))}"}`);
  } catch { /* empty */ }

  const [searchTemp, setSearchTemp] = useState<string>(params.serviceName || searchText || '');

  const paramSort = params.sort !== '' ? `${params.sort},${params.type}` : SORT_TYPE.UPDATED_TIMESTAMP_DESC;
  const filterData = {
    searchText: searchText !== '' ? searchText : params.serviceName,
    status: status !== '' ? status : params?.status || statusList.toString(),
    fromDate: fromDate !== null ? fromDate?.toString() : params.fromDate,
    toDate: toDate !== null ? toDate?.toString() : params.toDate,
    pageNo: serviceList.currentPageNumber,
    item: serviceList.numberOfRecordsPerPage,
    searchTitle: 'service_list_component.lbl_service_name_search',
    sort: sort !== '' ? sort : paramSort
  };

  const selectedExportItem = (item: IRecordModel[]) => {
    setSelectedServiceList(item);
    setExportSuccess(false);
  };

  const exportServiceHandler = async () => {
    try {
      setExportLoading(true);
      const resp = await exportServiceConfiguration(selectedServiceList);
      if (resp.data) {
        const contentDisposition = resp.request.getResponseHeader(exportConfigConst.contentDisposition_text);
        const filename = contentDisposition ? contentDisposition.split('filename="')[1].split('.')[0] : 'download';

        const url = window.URL.createObjectURL(
          new Blob([resp.data], { type: 'application/zip' })
        );

        // Create a temporary download link
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(exportConfigConst.download_text, `${filename}.zip`);

        // Simulate a click event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        link.dispatchEvent(clickEvent);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        success(
          translate('toast_component.lbl_success'),
          translate('service_list_wrapper_component.msg_export_service_config_success_body')
        );
        setExportSuccess(true);
        setExportLoading(false);
      }
    } catch (error: any) {
      let data;
      if (error?.response?.data) {
        data = await JSON.parse(arrayBufferToString(error?.response?.data));
      }

      setExportLoading(false);
      failure(
        translate('toast_component.lbl_error'),
        data?.errorMessage
      );
    }
  };

  const navigateToCreateServicePage = () => {
    dispatch(clearAllServiceList());
    const queryParams = new URLSearchParams(route.search).toString();
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`, { state: { path: `?${queryParams}` } });
  };

  const importServiceHandler = () => {
    dispatch(clearImportServiceSummary());
    const queryParams = new URLSearchParams(route.search).toString();
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.UPLOAD_FILE}`, { state: { path: `?${queryParams}` } });
  };

  const navigateToAiService = () => {
    history(URL_PATH.AI_SERVICE_HOME);
  };

  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });

  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    const filterStatusList: string[] = filterData?.status?.split(',') ?? [];
    if (!filterData) {
      return [];
    }
    if (filterStatusList?.length > 0 && filterStatusList?.length < allStatusList.length) {
      filterStatusList?.forEach((f: any) => data.push({ text: allStatusMap[f], type: FILTER_TYPE.STATUS, value: f }));
    }
    if (filterData.fromDate && filterData.toDate) {
      data.push({ value: filterData.toDate, type: FILTER_TYPE.DATE, text: `Last Modified: ${toLocalDate({ date: filterData.fromDate, dateTimeFormat: { date: true } })} - ${toLocalDate({ date: filterData.toDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.fromDate) {
      data.push({ value: filterData.fromDate, type: FILTER_TYPE.START_DATE, text: `Last Modified From: ${toLocalDate({ date: filterData.fromDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.toDate) {
      data.push({ value: filterData.toDate, type: FILTER_TYPE.END_DATE, text: `Last Modified To: ${toLocalDate({ date: filterData.toDate, dateTimeFormat: { date: true } })}` });
    }
    return data;
  }, [filterData]);

  const isShowingDefaultFilter = useMemo(
    () => {
      const filterStatusList: string[] = filterData?.status?.split(',') ?? [];
      const defaultSet = new Set(defaultServiceListStatusArray);
      const isDefaultStatuses = filterStatusList.length === defaultServiceListStatusArray.length && filterStatusList.every((s) => defaultSet.has(s));
      return isDefaultStatuses && !filterData?.fromDate && !filterData?.toDate;
    },
    [filterData]
  );

  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    let statusList: string[] = [];
    let hasStartDate = false;
    let hasEndDate = false;
    filters.forEach((f) => {
      if (f.type === FILTER_TYPE.STATUS) {
        statusList.push(f.value);
      } else if (f.type === FILTER_TYPE.START_DATE) {
        hasStartDate = true;
      } else if (f.type === FILTER_TYPE.END_DATE) {
        hasEndDate = true;
      } else if (f.type === FILTER_TYPE.DATE) {
        hasStartDate = true;
        hasEndDate = true;
      }
    });

    if (statusList.length === 0) {
      statusList = [...defaultServiceListStatusArray];
    }

    const data = {
      startDate: hasStartDate ? filterData?.fromDate : undefined,
      endDate: hasEndDate ? filterData?.toDate : undefined
    };

    const queryParam = {
      searchText: searchText !== '' ? searchText : searchTemp,
      status: statusList?.join(',') ?? '',
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
    };

    dispatch(addFilterServiceData(queryParam));
    dispatch(addClearData(ButtonProp.apply));
    dispatch(getAllServiceRequest(0, filterData.item, queryParam, sort));
  };

  const resetFiltersHandler = () => {
    const queryParam = {
      searchText: searchText !== '' ? searchText : searchTemp,
      status: defaultServiceListStatus,
      fromDate: '',
      toDate: '',
    };

    dispatch(addFilterServiceData(queryParam));
    dispatch(addClearData(ButtonProp.clear));
    dispatch(getAllServiceRequest(0, filterData.item, queryParam, sort));
  };

  const debounceHandler = useCallback(debounce((value: string) => {
    const queryParam = {
      searchText: value,
      status,
      toDate,
      fromDate,
    };
    dispatch(addFilterServiceData(queryParam));
    dispatch(addClearData(ButtonProp.search));
    dispatch(getAllServiceRequest(0, filterData.item, queryParam, SORT_TYPE.UPDATED_TIMESTAMP_DESC));
  }, debounceDelay), [searchTemp]);

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTemp((event.target.value).trim());
    debounceHandler((event.target.value).trim());
  };

  const onDependencyGraphFound = (isFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.DEPENDENCY_GRAPH_FOUND, addDependencyGraphPayload: { registryName, entityVersion, entityStatus, isDependencyGraphFound: isFound } });
  };

  const onCloseWarningClick = () => {
    dispatchDependencyState({ type: DEPENDENCY_ACTIONS.CLEAR_DEPENDENCY_GRAPH });
  };

  const navigateToServiceDependency = () => {
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.SERVICE_DEPENDENCY}`,
      { state: { ...route.state, associationServiceName: dependencyState.registryName, associationVersion: dependencyState.entityVersion, associationStatus: dependencyState.entityStatus } }
    );
  };

  const showDependencyGraphWarning = () => {
    if (dependencyState.isDependencyGraphFound) {
      return (
        <div className={classes['event-exist-warning']}>
          <div className={classes['warning-pad']} />
          <WarningMessage entityName={dependencyState.registryName} onHighlightFieldClick={navigateToServiceDependency} onCloseWarningClick={onCloseWarningClick} />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div>
        <BreadcrumbComponent />
        {renderToast()}
        <div className={classes['list-title']}>
          <CIPageHeader header={translate('service_list_wrapper_component.lbl_service')} />
          <div className={classes.filters}>
            <div className={classes['search-filter-div']}>
              <Filter
                dispatcherFunction={getAllServiceRequest}
                reducerFunction={addFilterServiceData}
                filterData={filterData}
                clearFunction={clearFilterService}
                clearAction={addClearData}
              />
              <div className={classes.search}>
                <SearchInput
                  label="search"
                  type="search"
                  placeholder={String(translate('service_list_wrapper_component.txt_search_service'))}
                  onChange={searchHandler}
                  data-testid="filterSearchInput"
                  value={searchTemp}
                />

              </div>
              <div className={`${classes.buttons} ci-button-group`}>
                {selectedServiceList?.length ? (
                  <Button
                    onClick={exportServiceHandler}
                    variant="secondary"
                    data-testid="exportServiceConfiguration"
                  >
                    <i className="bi bi-download" /> {translate('service_list_wrapper_component.btn_export_service_configuration')}
                  </Button>
                ) : (CIActionGuard([requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission) && (
                  <Button
                    variant="secondary"
                    onClick={importServiceHandler}
                    data-testid="importServiceConfiguration"
                  >
                    {translate('service_list_wrapper_component.btn_import_service_configuration')}
                  </Button>)
                )}
                {CIActionGuard([requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission) && (
                  <Button
                    variant="secondary"
                    onClick={navigateToAiService}
                    data-testid="aiServiceButton"
                  >
                    {translate('service_list_wrapper_component.btn_ai_service')}
                  </Button>
                )}
                {CIActionGuard([requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission) && (
                  <Button
                    variant="primary"
                    onClick={navigateToCreateServicePage}
                    className={classes['create-service-btn']}
                    data-testid="create-service-btn"
                  >
                    {translate('service_list_wrapper_component.btn_create_service')}
                  </Button>)}
              </div>
            </div>
            <CIAppliedListFilter
              className={classes['applied-filters']}
              totalRecords={listServices.totalRecords}
              appliedFilters={appliedFilterData}
              onFilterChange={appliedFilterChangeHandler}
              onResetFilters={resetFiltersHandler}
              showResetFiltersButton={!isShowingDefaultFilter}
            />
          </div>
        </div>
      </div>
      <div className={classes.body}>
        {showDependencyGraphWarning()}
        <ServiceList selectedExportItem={selectedExportItem} exportLoading={exportLoading} exportSuccess={exportSuccess} onDependencyGraphFound={onDependencyGraphFound} />
      </div>
    </>
  );
};

export default ServiceListWrapper;
